import { ChangeDetectionStrategy, Component, inject, input, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { combineLatest } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';

import { ButtonComponent } from '@shared/components/button/button.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { injectFlags } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { UnsavedChangesHelperService } from '@shared/services/unsaved-changes-helper.service';
import { Option } from '@shared/types/components.type';
import { selectWithGuardChangesModalCheck } from '@shared/utils/select-option';

import { MonthStats } from '../period-close.component';
import { ExpenseAnalyticCard } from './expense-analytic-card';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionTemplateDirective,
    ButtonComponent,
    ExpenseAnalyticCard,
  ],
  selector: 'aux-period-close-header',
  styleUrl: './period-close-header.component.scss',
  templateUrl: './period-close-header.component.html',
})
export class PeriodCloseHeaderComponent {
  overlayService = inject(OverlayService);
  periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  router = inject(Router);
  unsavedChangesHelperService = inject(UnsavedChangesHelperService);

  flags = injectFlags({
    showCloseSettingsLink: 'close_settings',
  });

  selectedMonth = toSignal(
    combineLatest([
      this.periodCloseService.isCurrentQuarterSelected.pipe(first()),
      this.periodCloseService.selectedQuarterMonthChanged$.pipe(startWith(null)),
    ]).pipe(
      map(() => {
        const date =
          (this.router.url.includes('expense-detail') ||
            this.router.url.includes('checklist') ||
            this.router.url.includes('journal-entries')) &&
          this.periodCloseService.persistedQuarterMonth
            ? dayjs(this.periodCloseService.persistedQuarterMonth)
            : dayjs(this.periodCloseService.selectedQuarterMonth);

        return date.isValid() ? date.format('MMMM YYYY') : '';
      })
    ),
    {
      initialValue: '',
    }
  );

  months = input.required<MonthStats[]>();
  quarters = input.required<Option[]>();
  selectedQuarter = input.required<FormControl>();

  selectedQuarterEl = viewChild<NgSelectComponent>('selectedQuarterEl');

  onCloseSettingsClick(): void {
    void this.router.navigate([
      `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.INDEX}`,
    ]);
  }

  async selectQuarter(event: MouseEvent, value: string): Promise<void> {
    await selectWithGuardChangesModalCheck({
      element: this.selectedQuarterEl()!,
      event,
      formControl: this.selectedQuarter()!,
      hasUnsavedChanges: this.unsavedChangesHelperService.getUnsavedChangesDecisionByRouterUrl(),
      overlayService: this.overlayService,
      value,
    });
  }
}
